import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../store/index';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  SvgIcon,
  CardActions,
  Container,
} from '@material-ui/core';
import { Helmet } from 'react-helmet';
import ProjectionsView from './ProjectionsView/ProjectionsView';
import QualityView from './QualityView/QualityView';
import DanaiTab from './DanaiView/DanaiTab';
import ResultDialogTitle from './Title/ResultDialogTitle';
import QualityIcon from '../../../resources/icons/BQuality.png';
import BDiagnose from '../../../resources/icons/BDiagnose.png';
import { ReactComponent as Summary_Icon } from '../../../resources/icons/mammography_icon.svg';
import SummaryCardActions from './CardActions/SummaryCardActions';
import ReviseCardActions from './CardActions/ReviseCardActions';
import RevisionTools from './Common/RevisionTools';
import Draggable from 'react-draggable';
import InRevisionDialog from './CardActions/InRevisionDialog';
import { setCustomResults } from '../../../store/slices/labeling';
import SummaryFront from './Summary/SummaryFront';
import { handleRevisionDiscard, handleRevisionSave } from '../../../store/slices/labeling';
import { fetchPost, fetchGet } from '../../../helpers/fetching';

const useStyles = makeStyles((theme) => ({
  main_container: {
    overflow: 'hidden',
    flexGrow: 1,
    width: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.darkerBg,
    height: '96vh',
  },

  main_container_portrait: {
    overflow: 'hidden',
    flexGrow: 1,
    width: '93vw',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.darkerBg,
    height: '100%',
    padding: '10px',
  },
  result_view_main_container: {
    width: '100%',
    height: '100%',
  },

  summary_container: {
    height: '100%',
    borderRadius: '5px',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    maxHeight: '100vh',
  },

  header_container: {
    borderRadius: '5px 5px 0 0 ',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    padding: '0',
    height: '38px',
    minHeight: '38px',
    backgroundColor: theme.palette.texts.generalTitleBackgrounds,
  },

  header_container_tabs: {
    padding: 0,
    borderRadius: '5px',
    maxWidth: '100%',
    height: '36px',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  card_container_summary: {
    justifyItems: 'center',
    height: '100%',
    overflow: 'hidden',
    padding: '0 !important',
    maxHeight: '100vh',
  },

  card_container: {
    justifyItems: 'center',
    height: '100%',
    overflow: 'hidden',
    maxHeight: '98vh',
    padding: '0 !important',
  },

  placeholder_container: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    background: theme.palette.background.mainDark,
    borderRadius: '5px',
    justifyContent: 'center',
    flexDirection: 'column',
  },

  main_card_details_container: {
    width: '100%',
    height: '100%',
    borderRadius: '5px',
    overflowY: 'hidden',
  },

  placeholder_helper: {
    color: 'gray',
    fontSize: theme.fonts.body1,
    maxWidth: '70%',
  },

  placeholder_title: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: '#b8b8b8',
    fontSize: theme.fonts.responsiveHeader,
  },

  header_icon: {
    width: '20px',
  },

  header_icon_disabled: { width: '20px', opacity: 0.3 },

  titleResultCard: {
    marginLeft: '10px',
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveBig,
    alignSelf: 'center',
    minWidth: 'fit-content',
    textTransform: 'none',
  },

  title_danai: {
    marginLeft: '10px',
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveBig,
    alignSelf: 'center',
    minWidth: 'fit-content',
    textTransform: 'none',
    color: 'gray',
  },

  tab_buttons: {
    height: '100%',
    borderRadius: 0,
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
  },

  dragable_tool: {
    padding: '12px 6px',
    minHeight: '5vh',
    position: 'absolute',
    boxShadow: 'rgb(19 19 19 / 78%) 0px 0px 6px 2px',
    background: theme.palette.background.darkerBg,
    display: 'flex',
    // flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-around',
    cursor: 'move',
    zIndex: 1000,
    borderRadius: '5px',
  },
}));

function ResultView(props) {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { token, study_id, username, password } = useParams();
  const history = useHistory();

  // Store State
  const results = useSelector((state) => state.results);
  const quality_module = useSelector((state) => state.global_settings.premium_features.quality_module);
  const labeling = useSelector((state) => state.labeling);
  const { exam_list, currentExam } = useSelector((state) => state.examinations);
  const { tabsIndex, examinationListDrawer, activeDialog, darkState, landscape } = useSelector(
    (state) => state.gui,
  );

  const {
    applied_density,
    applied_opacities,
    applied_microcalc,
    shown_density,
    shown_microcalc,
    shown_opacities,
    shown_quality,
    applied_quality,
    patient_id,
    report_sending_rules,
    hl7_sending_rules,
    fhir_sending_rules,
    error,
  } = results;

  const {
    qualityInRevisionMode,
    qualityInDrawingMode,
    densityInRevisionMode,
    diagnosticsInRevisionMode,
    localChangesDensity,
    localChangesQuality,
    localChangesMicrocalc,
    localChangesOpacities,
    customOpacities,
  } = labeling;

  //Variables

  const showRevisionTool = tabsIndex === 2 && qualityInRevisionMode && qualityInDrawingMode;

  const someLocalChanges =
    localChangesDensity || localChangesQuality || localChangesMicrocalc || localChangesOpacities;

  const someRevisionMode = densityInRevisionMode || diagnosticsInRevisionMode || qualityInRevisionMode;

  useEffect(() => {
    if (!quality_module && tabsIndex === 1) dispatch(actions.setTabsIndex(0));
  }, [quality_module, tabsIndex, dispatch]);

  async function authenticateURLToken(token) {
    const response = await fetchPost('login', { token });
    if (response.success) {
      localStorage.setItem('username', response.username);
      localStorage.setItem('role', response.role);
      localStorage.setItem('annotation', response.annotation);
      localStorage.setItem('diagnostics', response.diagnostics);
      localStorage.setItem('aliases', response.aliases);
      localStorage.setItem('last_login', response.last_login);
      console.log('You are authorized', study_id);
      dispatch(actions.setCurrentExam(study_id));
    } else {
      console.log('Token not recognized. You shall no pass!');
      history.push('/logout');
    }
  }

  async function authenticateURLBasic(username, password) {
    const response = await fetchPost('login', { login: username, password });
    if (response.success) {
      localStorage.setItem('username', response.username);
      localStorage.setItem('role', response.role);
      localStorage.setItem('annotation', response.annotation);
      localStorage.setItem('diagnostics', response.diagnostics);
      localStorage.setItem('aliases', response.aliases);
      localStorage.setItem('last_login', response.last_login);
      console.log('You are authorized');
      dispatch(actions.setCurrentExam(study_id));
    } else {
      console.log('Credentials not recognized. You shall no pass!');
      history.push('/logout');
    }
  }

  useEffect(() => {
    dispatch(setCustomResults(results));
    if (!shown_quality) dispatch(actions.setShownQuality(applied_quality || 'bbox'));
    if (!shown_density) dispatch(actions.setShownDensity(applied_density || 'bbox'));
    if (!shown_microcalc) dispatch(actions.setShownMicrocalc(applied_microcalc || 'bbox'));
    if (!shown_opacities) dispatch(actions.setShownOpacities(applied_opacities || 'bbox'));
  }, [results]);

  useEffect(() => {
    dispatch(actions.setLoadedProjections(0));
    dispatch(actions.setZoomedProjection(null));
    dispatch(actions.setInitialResult());
    dispatch(actions.hideLinesShown());

    if (!!currentExam) {
      dispatch(actions.fetchResults());
      if (!token && !username && !password && study_id !== currentExam) {
        history.push(`/bbox/results/details/${currentExam}`);
      }
    } else if (!token && !username && !password && !!study_id) {
      history.push('/bbox/results/details');
    }
  }, [currentExam]);

  useEffect(() => {
    if (!currentExam && exam_list.length > 0) {
      dispatch(actions.setCurrentExam(exam_list[0].id));
    } else if (!study_id && exam_list.length === 0) {
      dispatch(actions.setCurrentExam(null));
    }
  }, [dispatch, exam_list]);

  useEffect(() => {
    if (tabsIndex === 0) {
      dispatch(actions.setDensityInRevisionMode(false));
      dispatch(actions.setDiagnosticsInRevisionMode(false));
      dispatch(actions.setQualityInRevisionMode(false));
      dispatch(actions.setZoomedProjection(null));
      dispatch(actions.setQualityInDrawingMode(false));
    }
  }, [currentExam, tabsIndex]);

  useEffect(() => {
    if (!token && !username && !password && !!study_id && study_id !== currentExam) {
      dispatch(actions.setCurrentExam(study_id));
    }
  }, [study_id]);

  useEffect(() => {
    if (!token) return;
    authenticateURLToken(token);
  }, [token]);

  useEffect(() => {
    if (!username || !password) return;
    authenticateURLBasic(username, password);
  }, [username, password]);

  // Group opacities
  function SummaryTabIcon(props) {
    return (
      <SvgIcon {...props}>
        <Summary_Icon />
      </SvgIcon>
    );
  }

  const resultViewCardActions = () => {
    if (someRevisionMode) {
      return <ReviseCardActions />;
    } else return <SummaryCardActions />;
  };

  const discardChanges = () => {
    dispatch(handleRevisionDiscard());
  };

  const saveLocalChanges = () => {
    dispatch(handleRevisionSave(t));
  };

  const handleActionWithChangesCheck = (action) => {
    if (someLocalChanges) {
      dispatch(actions.setActiveDialog('unsavedChanges'));
    } else {
      // discardChanges();
      action();
    }
  };

  return (
    <Container
      maxWidth={false}
      className={landscape ? classes.main_container : classes.main_container_portrait}
      style={{
        borderTopLeftRadius: '5px',
        padding: `8px 4px 0`,
      }}>
      <Helmet>
        <title>{t('b_box.pat_details')}</title>
        <meta name='patient_details' />
      </Helmet>
      {activeDialog === 'unsavedChanges' && (
        <InRevisionDialog
          open={activeDialog === 'unsavedChanges'}
          onDiscard={() => {
            discardChanges();
            dispatch(actions.setActiveDialog(null));
          }}
          onClose={() => dispatch(actions.setActiveDialog(null))}
          onSave={() => {
            saveLocalChanges();
            dispatch(actions.setActiveDialog(null));
          }}
        />
      )}
      {showRevisionTool && (
        <Draggable
          bounds='parent'
          axis='both'
          defaultPosition={{
            x: landscape ? (examinationListDrawer ? 48 : -45) : examinationListDrawer ? 20 : -25,
            y: 750,
          }}>
          <div className={classes.dragable_tool}>
            <RevisionTools />
          </div>
        </Draggable>
      )}
      <Grid
        container
        spacing={1}
        className={classes.result_view_main_container}
        style={{
          backgroundColor: theme.palette.background.default,
          overflowY: landscape ? 'hidden' : 'scroll',
        }}>
        <Grid item xs={landscape ? 5 : 12}>
          <Card className={classes.summary_container} elevation={0}>
            <CardHeader
              className={classes.header_container}
              title={
                <ResultDialogTitle
                  titlePrevious={dispatch(actions.getNextPatientName(-1)) || ''}
                  onClickBackward={() => {
                    handleActionWithChangesCheck(() => dispatch(actions.switchExamResult(-1)));
                  }}
                  onClickForward={() => {
                    handleActionWithChangesCheck(() => dispatch(actions.switchExamResult(1)));
                  }}
                  titleNext={dispatch(actions.getNextPatientName(1)) || ''}
                />
              }
            />
            <CardContent className={classes.card_container}>
              <ProjectionsView />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={landscape ? 7 : 12} style={{ maxHeight: '100%' }}>
          <Card className={classes.summary_container} elevation={0}>
            <CardContent className={classes.card_container_summary}>
              {(!currentExam && !token && !username) || error ? (
                <div className={classes.placeholder_container}>
                  <Typography className={classes.placeholder_title} color='primary' gutterBottom>
                    {error ? t('api_messages.exam_not_existing') : t('ResultView.empty')}
                  </Typography>
                  <Typography className={classes.placeholder_helper} color='primary' gutterBottom>
                    {error ? t('api_messages.exam_not_existing_helper') : t('ResultView.placeholder')}
                  </Typography>
                </div>
              ) : (
                <div className={classes.main_card_details_container}>
                  {/* Summary Tab */}
                  {(tabsIndex === 0 || !tabsIndex || (tabsIndex === 2 && !someRevisionMode)) && (
                    <SummaryFront />
                  )}

                  {/* Quality Tab */}
                  {quality_module && tabsIndex === 1 && <QualityView />}

                  {/* Diagnostic and density Tab */}
                  {tabsIndex === 2 && someRevisionMode && <DanaiTab />}
                </div>
              )}
            </CardContent>

            {currentExam && (
              <CardActions
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: darkState ? theme.palette.background.mainDark : 'rgb(209, 209, 209)',
                  borderRadius: '5px',
                  padding: '0',
                  marginTop: '8px',
                  marginBottom: !landscape && '20px',
                  minHeight: !landscape && '40px',
                }}>
                {resultViewCardActions()}
              </CardActions>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default React.memo(ResultView);
